import { SyntheticEvent, useState } from 'react';
import classNames from 'classnames';
import { Button, ButtonSizeSM, ButtonSkinTertiary, ButtonVariants } from '@picsart/button';
import IconCrossLarge from '@picsart/icons/IconCrossLarge';
import { useTranslator } from '@picsart/rc/services/localization';
import { View } from '@picsart/view';
import { Spacings, Typography } from '@picsart/cascade';
import Text from '@picsart/text/Text';
import TextField from '@picsart/textfield/TextField';
import { ControlSizesMD } from '@picsart/inlineButton';

import useMount from 'hooks/common/useMount';
import useDownload from 'hooks/useDownload';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import { IMAGE_MIME_TYPES } from 'pages/Batch/configs';
import analytics from 'pages/Batch/utils/analytics';

import useStyles from './styles';
import testIds from './testIds';

interface IDownloadImageMenu {
  onClose: (viaDownload?: boolean) => void;
  showZipNameField: boolean;
}

export const DownloadImageMenu = ({ onClose, showZipNameField }: IDownloadImageMenu) => {
  const { downloadImages } = useDownload();
  const { isLoading } = useBatchData();
  const classes = useStyles();
  const translate = useTranslator();
  const [activeFormat, setActiveFormat] = useState<IMAGE_MIME_TYPES>(IMAGE_MIME_TYPES.PNG);
  const [fileName, setFileName] = useState('Untitled');

  useMount(() => {
    analytics.exportOpen();
  });

  const handleChangeFileName = (event: SyntheticEvent<HTMLInputElement, Event>) => {
    setFileName(event.currentTarget.value);
  };

  const handleDownload = () => {
    downloadImages(activeFormat, fileName.trim());
    analytics.exportAction('download', activeFormat);
    onClose(true);
  };

  return (
    <View
      flexDirection="column"
      width="343px"
      minWidth="100%"
      maxWidth="100%"
    >
      <View
        alignItems="center"
        justifyContent="space-between"
        paddingBlockEnd={Spacings.s16}
      >
        <Text size={Typography.t6}>{translate('Download')}</Text>
        <Button
          size={ButtonSizeSM}
          centerIcon={IconCrossLarge}
          skin={ButtonSkinTertiary}
          onClick={() => onClose()}
        />
      </View>
      {showZipNameField && (
        <View
          flexDirection="column"
          rowGap={Spacings.s6}
          paddingBlockEnd={Spacings.s24}
        >
          <Text size={Typography.t4}>{translate('Zip name')}</Text>
          <TextField size={ControlSizesMD} value={fileName} onChange={handleChangeFileName} />
        </View>
      )}
      <View
        flexDirection="column"
        rowGap={Spacings.s6}
        paddingBlockEnd={Spacings.s24}
      >
        {showZipNameField && <Text size={Typography.t4}>{translate('File type')}</Text>}
        <View columnGap={Spacings.s8}>
          <Button
            isFullWidth
            variant={ButtonVariants.Outline}
            onClick={() => setActiveFormat(IMAGE_MIME_TYPES.JPEG)}
            isActive={activeFormat === IMAGE_MIME_TYPES.JPEG}
            className={classNames(classes.formatButton, {
              [classes.activeFormatButton]: activeFormat === IMAGE_MIME_TYPES.JPEG,
            })}
            dataTestId={testIds.selectJpgBtn}
          >
            {translate('JPG')}
          </Button>
          <Button
            isFullWidth
            variant={ButtonVariants.Outline}
            onClick={() => setActiveFormat(IMAGE_MIME_TYPES.PNG)}
            className={classNames(classes.formatButton, {
              [classes.activeFormatButton]: activeFormat === IMAGE_MIME_TYPES.PNG,
            })}
            dataTestId={testIds.selectPngBtn}
          >
            {translate('PNG')}
          </Button>
        </View>
      </View>
      <Button
        isFullWidth
        onClick={handleDownload}
        isDisabled={fileName.trim().length === 0 || isLoading}
        isLoading={isLoading}
        dataTestId={testIds.downloadBtn}
      >
        {translate('Download')}
      </Button>
    </View>
  );
};
